import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function DonatePage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    amount: '',
    customAmount: '',
    donationType: 'one-time',
    program: 'general',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const suggestedAmounts = [25, 50, 100, 250, 500, 1000];

  const programs = [
    { value: 'general', label: 'Where Needed Most' },
    { value: 'health-screenings', label: 'Health Screenings' },
    { value: 'education', label: 'Education Workshops' },
    { value: 'mental-health', label: 'Mental Health Support' },
    { value: 'community-outreach', label: 'Community Outreach' },
    { value: 'youth-programs', label: 'Youth Programs' },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAmountClick = (amount: number) => {
    setFormData(prev => ({ ...prev, amount: amount.toString(), customAmount: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const finalAmount = formData.customAmount || formData.amount;

    if (!finalAmount || parseFloat(finalAmount) <= 0) {
      setSubmitMessage('Please enter a valid donation amount.');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 500) {
      setSubmitMessage('Message must be 500 characters or less.');
      setIsSubmitting(false);
      return;
    }

    try {
      const submitData = new URLSearchParams();
      submitData.append('firstName', formData.firstName);
      submitData.append('lastName', formData.lastName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('amount', finalAmount);
      submitData.append('donationType', formData.donationType);
      submitData.append('program', formData.program);
      submitData.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d61t8d950i17hner1rd0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: submitData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for your generous donation! We will contact you shortly with payment details.');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          amount: '',
          customAmount: '',
          donationType: 'one-time',
          program: 'general',
          message: '',
        });
      } else {
        setSubmitMessage('Something went wrong. Please try again or contact us directly.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-coral/10 via-white to-sage/10 pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-charcoal mb-6">
              Make a Difference Today
            </h1>
            <p className="text-lg text-charcoal/70 leading-relaxed">
              Your generous contribution helps us provide vital health screenings, education workshops, and support services to underserved communities. Every donation creates lasting impact.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: 'ri-heart-pulse-line', number: '2,500+', label: 'Lives Impacted', description: 'Through health screenings and education' },
              { icon: 'ri-community-line', number: '150+', label: 'Events Hosted', description: 'Bringing wellness to communities' },
              { icon: 'ri-hand-heart-line', number: '95%', label: 'Direct Impact', description: 'Of donations go directly to programs' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 rounded-2xl bg-gradient-to-br from-coral/5 to-sage/5 border border-coral/10"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-coral/10 rounded-full mx-auto mb-4">
                  <i className={`${stat.icon} text-3xl text-coral`}></i>
                </div>
                <div className="font-serif text-4xl font-bold text-charcoal mb-2">{stat.number}</div>
                <div className="font-semibold text-charcoal mb-2">{stat.label}</div>
                <p className="text-sm text-charcoal/60">{stat.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-20 bg-gradient-to-br from-sage/5 to-coral/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-xl p-8 md:p-12"
          >
            <h2 className="font-serif text-3xl font-bold text-charcoal mb-8 text-center">
              Complete Your Donation
            </h2>

            <form onSubmit={handleSubmit} data-readdy-form id="donation-form">
              {/* Donation Type */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-charcoal mb-3">Donation Type</label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, donationType: 'one-time' }))}
                    className={`py-3 px-6 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap ${
                      formData.donationType === 'one-time'
                        ? 'bg-coral text-white shadow-md'
                        : 'bg-gray-50 text-charcoal hover:bg-gray-100'
                    }`}
                  >
                    One-Time
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, donationType: 'monthly' }))}
                    className={`py-3 px-6 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap ${
                      formData.donationType === 'monthly'
                        ? 'bg-coral text-white shadow-md'
                        : 'bg-gray-50 text-charcoal hover:bg-gray-100'
                    }`}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              {/* Donation Amount */}
              <div className="mb-8">
                <label className="block text-sm font-semibold text-charcoal mb-3">Select Amount</label>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {suggestedAmounts.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => handleAmountClick(amount)}
                      className={`py-3 px-4 rounded-xl font-medium transition-all cursor-pointer whitespace-nowrap ${
                        formData.amount === amount.toString() && !formData.customAmount
                          ? 'bg-coral text-white shadow-md'
                          : 'bg-gray-50 text-charcoal hover:bg-gray-100'
                      }`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal font-medium">$</span>
                  <input
                    type="number"
                    name="customAmount"
                    value={formData.customAmount}
                    onChange={handleInputChange}
                    placeholder="Enter custom amount"
                    min="1"
                    step="0.01"
                    className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm"
                  />
                </div>
              </div>

              {/* Program Selection */}
              <div className="mb-8">
                <label htmlFor="program" className="block text-sm font-semibold text-charcoal mb-3">
                  Support a Specific Program
                </label>
                <select
                  id="program"
                  name="program"
                  value={formData.program}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm cursor-pointer"
                >
                  {programs.map((program) => (
                    <option key={program.value} value={program.value}>
                      {program.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Personal Information */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-semibold text-charcoal mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-semibold text-charcoal mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-charcoal mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-charcoal mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all text-sm"
                  />
                </div>
              </div>

              {/* Message */}
              <div className="mb-8">
                <label htmlFor="message" className="block text-sm font-semibold text-charcoal mb-2">
                  Message (Optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={4}
                  placeholder="Share why you're supporting 1 Heart Health and Wellness..."
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none transition-all resize-none text-sm"
                ></textarea>
                <div className="text-xs text-charcoal/50 mt-1 text-right">
                  {formData.message.length}/500 characters
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-coral text-white py-4 rounded-xl font-semibold hover:bg-coral/90 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 cursor-pointer whitespace-nowrap text-sm"
              >
                {isSubmitting ? 'Processing...' : 'Complete Donation'}
                <i className="ri-heart-fill ml-2"></i>
              </button>

              {submitMessage && (
                <div
                  className={`mt-4 p-4 rounded-xl text-sm ${
                    submitMessage.includes('Thank')
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {submitMessage}
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      {/* Ways to Give */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-charcoal mb-4">Other Ways to Give</h2>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              There are many ways to support our mission and make a lasting impact
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-building-line',
                title: 'Corporate Sponsorship',
                description: 'Partner with us to support community wellness initiatives and gain visibility for your brand.',
                link: '/contact',
              },
              {
                icon: 'ri-gift-line',
                title: 'In-Kind Donations',
                description: 'Donate medical supplies, equipment, or services to directly support our health programs.',
                link: '/contact',
              },
              {
                icon: 'ri-calendar-event-line',
                title: 'Host a Fundraiser',
                description: 'Organize an event in your community to raise awareness and funds for our cause.',
                link: '/contact',
              },
            ].map((way, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-sage/5 to-coral/5 rounded-2xl p-8 border border-coral/10 hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-coral/10 rounded-full mb-6">
                  <i className={`${way.icon} text-2xl text-coral`}></i>
                </div>
                <h3 className="font-serif text-xl font-bold text-charcoal mb-3">{way.title}</h3>
                <p className="text-charcoal/70 mb-6 text-sm leading-relaxed">{way.description}</p>
                <Link
                  to={way.link}
                  className="inline-flex items-center text-coral font-semibold hover:gap-2 transition-all cursor-pointer text-sm whitespace-nowrap"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tax Information */}
      <section className="py-16 bg-gradient-to-br from-coral/5 to-sage/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl p-8 shadow-md">
            <i className="ri-shield-check-line text-4xl text-coral mb-4"></i>
            <h3 className="font-serif text-2xl font-bold text-charcoal mb-3">Tax-Deductible Donation</h3>
            <p className="text-charcoal/70 leading-relaxed text-sm">
              1 Heart Health and Wellness is a registered 501(c)(3) nonprofit organization. Your donation is tax-deductible to the fullest extent allowed by law. You will receive a receipt for your records after your donation is processed.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
